import ReplyWithTextFeatures from '../contract/ReplyWithTextFeatures';
import { createInitialComposeContentBlock } from 'owa-mail-compose-store/lib/utils/getDefaultComposeContentBlock';
import { getSelectedTableView } from 'owa-mail-list-store';
import isReadingPaneBottom from 'owa-mail-reading-pane-store/lib/utils/isReadingPaneBottom';
import { isDeepLink } from 'owa-popout';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import { trace } from 'owa-trace';
import { action } from 'satcheljs/lib/legacy';
import {
    findInlineComposeViewState,
    getDefaultBodyType,
    lazyReplyToMessageAndCreateViewState,
    lazyFindComposePlugins,
    TEXT_BODY_TYPE,
} from 'owa-mail-compose-store';

const QUOTE_CLASS = 'userQuote';
const SMART_REPLY_CLASS = 'smartReply';

export default action('replyWithText')(async function replyWithText(
    itemId: string,
    conversationId: string,
    selection: string,
    feature: ReplyWithTextFeatures
) {
    const elementClass = getElementClassName(feature);
    const elementId = elementClass + Math.round(Math.random() * 1000000).toString();

    // Don't use HTML if we're in plain text mode.
    const isPlainText: boolean = getDefaultBodyType() === TEXT_BODY_TYPE;
    const selectionAsHtml: string = createHtmlForText(feature, elementId, selection);
    const contentToInsert: string = isPlainText
        ? selection
        : createInitialComposeContentBlock(selectionAsHtml);

    const existingViewState = findInlineComposeViewState(conversationId);
    let viewState = null;

    if (existingViewState) {
        // If the compose view state already exists, this means the compose window is open
        // Since the compose window is already open, just insert quoted content into email
        viewState = existingViewState;
    } else {
        // Otherwise, create a new reply with quoted content
        const replyToMessageAndCreateViewState = await lazyReplyToMessageAndCreateViewState.import();
        viewState = await replyToMessageAndCreateViewState({
            referenceItemOrId: itemId,
            isReplyAll: true,
            useFullCompose:
                getListViewType() == ReactListViewType.Message ||
                isReadingPaneBottom() ||
                isDeepLink(),
            conversationId: conversationId,
            body: contentToInsert,
            instrumentationContexts: [],
            actionSource: 'ReplyWithText',
        });
    }

    if (viewState) {
        const findComposePlugins = await lazyFindComposePlugins.tryImportSync();
        if (findComposePlugins) {
            const composePlugins = findComposePlugins(viewState);
            if (composePlugins && composePlugins.contentOperationPlugin) {
                if (existingViewState) {
                    composePlugins.contentOperationPlugin.insertContentIntoEditor(
                        isPlainText ? selection : selectionAsHtml,
                        getEventName(feature),
                        false /* belowExistingContent */,
                        true /* setFocusToEditor */
                    );
                } else if (!isPlainText) {
                    // Note: This logic doesn't work in plain text mode, as we don't have an HTML element to reference.
                    // In plain text mode, any additional smart replies will be inserted in the front of the item rather than the end, which
                    // is undesirable but also not a common case.
                    // TODO VSO 42231 for fixing this issue
                    composePlugins.contentOperationPlugin.moveCaretBelowHtmlElement(elementId);
                }
            }
        }
    }
});

function getElementClassName(feature: ReplyWithTextFeatures): string {
    switch (feature) {
        case ReplyWithTextFeatures.UserHighlighting:
            return QUOTE_CLASS;
        case ReplyWithTextFeatures.SmartReply:
            return SMART_REPLY_CLASS;
        default:
            trace.warn('replyWithText_getElementClassName: Unsupported feature inputted');
            return '';
    }
}

function getEventName(feature: ReplyWithTextFeatures): string {
    switch (feature) {
        case ReplyWithTextFeatures.UserHighlighting:
            return 'UserHighlighting';
        case ReplyWithTextFeatures.SmartReply:
            return 'SmartReply';
        default:
            trace.warn('replyWithText_getEventName: Unsupported feature inputted');
            return '';
    }
}

function createHtmlForText(
    feature: ReplyWithTextFeatures,
    elementId: string,
    textForMarkup: string
): string {
    switch (feature) {
        case ReplyWithTextFeatures.UserHighlighting:
            return createQuoteTextHtml(elementId, textForMarkup);
        case ReplyWithTextFeatures.SmartReply:
            return createSmartReplyHTML(elementId, textForMarkup);
        default:
            trace.warn('replyWithText_createHtmlForText: Unsupported feature inputted');
            return '';
    }
}

function createQuoteTextHtml(quoteId: string, quoteText: string): string {
    // TODO: Reference the same function as the blockquote button
    // in the ribbon (VSO 36144)

    const quoteElement = document.createElement('blockquote');
    quoteElement.id = quoteId;
    quoteElement.className = QUOTE_CLASS;
    quoteElement.style.borderLeft = '3px solid #C8C8C8';
    quoteElement.style.paddingLeft = '1ex';
    quoteElement.style.marginLeft = '0.8ex';
    quoteElement.style.color = '#666666';
    quoteElement.innerHTML = quoteText + '<br/>';

    const elementWrapper = document.createElement('div');
    elementWrapper.appendChild(document.createElement('br'));
    elementWrapper.appendChild(quoteElement);
    elementWrapper.appendChild(document.createElement('br'));

    return elementWrapper.innerHTML;
}

function createSmartReplyHTML(smartReplyId: string, smartReplyText: string): string {
    const smartReplyElement = document.createElement('span');
    smartReplyElement.id = smartReplyId;
    smartReplyElement.innerText = smartReplyText;

    const elementWrapper = document.createElement('div');
    elementWrapper.appendChild(smartReplyElement);

    return elementWrapper.innerHTML;
}

function getListViewType(): ReactListViewType {
    let listViewType = null;
    const tableView = getSelectedTableView();
    if (tableView && tableView.tableQuery) {
        listViewType = tableView.tableQuery.listViewType;
    }
    return listViewType;
}
